import { ActivatedRoute, NavigationEnd, Params, Router } from '@angular/router';
import { Observable } from 'rxjs';
import {
    distinctUntilChanged,
    filter,
    map,
    pairwise,
    startWith,
} from 'rxjs/operators';

function watchParams(
    obs: Observable<Params>,
    startValue: Params = {},
): Observable<Params> {
    return obs.pipe(startWith<Params, Params>(startValue), pairwise());
}

export function watchForNumberParamChange(
    activatedRoute: ActivatedRoute,
    key: string,
    startValue: number,
    distinct = true,
): Observable<number> {
    return watchParams(activatedRoute.queryParams, { [key]: startValue }).pipe(
        filter((queryParams) => {
            if (distinct) {
                let a = parseInt(queryParams[1][key]);
                let b = parseInt(queryParams[0][key]);
                a = isNaN(a) ? null : a;
                b = isNaN(b) ? null : b;
                return a !== b;
            }
            return true;
        }),
        map((queryParams) => {
            const value = parseInt(queryParams[1][key]);
            return isNaN(value) ? null : value;
        }),
    );
}

export function watchForUserChange(
    activatedRoute: ActivatedRoute,
): Observable<number> {
    return watchParams(activatedRoute.params).pipe(
        filter(
            (params) =>
                parseInt(params[1].userId) !== parseInt(params[0].userId),
        ),
        map((params) => {
            return parseInt(params[1].userId);
        }),
        distinctUntilChanged(),
    );
}

type SubOutletLocation = [
    'observation' | 'commitment' | 'draftcommitment',
    string,
];

export function watchForSubOutletChange(
    router: Router,
): Observable<SubOutletLocation> {
    return router.events.pipe(
        filter<NavigationEnd>((e) => e instanceof NavigationEnd),
        map((e) => e.url),
        startWith<string, string>(router.url),
        pairwise(),
        startWith([null, router.url]),
        map((urls) => {
            let match =
                /\/(observation|commitment|commitments)\/(\d+)(?:\/drafts\/(.+?)|)(?:\?|\/|$)/i.exec(
                    urls[1],
                );
            if (match && match.length >= 4) {
                return [
                    match[1],
                    `${match[2]}--${match[3]}`,
                ] as SubOutletLocation;
            }
            match = /\/(draftcommitments)\/(.+?)(?:\?|\/|$)/i.exec(urls[1]);
            if (match && match.length >= 3) {
                return [
                    match[1],
                    `undefined--${match[2]}`,
                ] as SubOutletLocation;
            }
            match = /\/(observation|draftcommitments)(?:\?|\/|$)/i.exec(
                urls[1],
            );
            if (match && match.length >= 2) {
                return [match[1], 'new'] as SubOutletLocation;
            }
            return [null, null] as SubOutletLocation;
        }),
        distinctUntilChanged(),
    );
}
